import { Component, ElementRef, OnInit } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';
import { DRAFTS_PAGE_URL, draftsTabUrl } from 'src/app/data/draft-tabs';

type VariableFormat = {
  color: string;
  background: string;
};

type FormatRange = VariableFormat & {
  index: number;
  length: number;
};

type DraftEditor = {
  getSelection: (focus?: boolean) => { index: number; length: number } | null;
  getLength: () => number;
  insertText: (
    index: number,
    text: string,
    formats?: { color?: string | boolean; background?: string | boolean },
    source?: string
  ) => void;
  deleteText: (index: number, length: number, source?: string) => void;
  getText: (index?: number, length?: number) => string;
  getContents: () => {
    ops?: Array<{
      insert?: string | Record<string, unknown>;
      attributes?: Record<string, string>;
    }>;
  };
  getFormat: (
    index: number,
    length?: number
  ) => Record<string, string | undefined>;
  formatText: (
    index: number,
    length: number,
    formats: Record<string, string | boolean>,
    source?: string
  ) => void;
  removeFormat: (index: number, length: number, source?: string) => void;
  format: (name: string, value: string | boolean, source?: string) => void;
  setSelection: (index: number, length: number, source?: string) => void;
  focus: () => void;
  on: (
    event: string,
    handler: (range: { index: number; length: number } | null) => void
  ) => void;
};

@Component({
  selector: 'app-dashboard-drafts-form',
  templateUrl: './dashboard-drafts-form.component.html',
  styleUrls: ['./dashboard-drafts-form.component.scss'],
})
export class DashboardDraftsFormComponent implements OnInit {
  form = this.formBuilder.group({
    name: [null, [Validators.required]],
    type: [null, [Validators.required]],
    assignees: [[], []],
    witnesses: [[], []],

    final_date: [
      null,
      [
        Validators.required,
        Utils.validateMaxDate,
        (control: FormControl) => Utils.validateMaxMinToday(control, false),
      ],
    ],
    enabled: [true, []],

    content: [null, [Validators.required]],
  });

  searchForm = this.formBuilder.group({
    search: [null, []],
  });

  applicableItems = [];

  coobligationItems = [
    { label: 'Sem coobrigação', value: false },
    { label: 'Com coobrigação', value: true },
  ];

  typeItems = [];
  witnessItems = [];

  statusItems = [
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false },
  ];

  id = null;
  fundId = null;

  templateId = null;

  quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link'],
      ['clean'],
    ],
  };

  types: any[] = [];
  showForm = true;
  showResume = false;

  loading = true;
  previewing = false;

  title = '';

  variables: VariableValues[] = [];
  witnessVariables: VariableValues[] = [];
  variablesTabIndex = 0;
  variableTabs: Tab[] = [
    { label: 'Variáveis', value: 'variables' },
    { label: 'Testemunhas', value: 'witnesses' },
  ];
  private allVariables: VariableValues[] = [];
  count = 0;
  offset = 0;
  variablesPage = 1;
  witnessPage = 1;
  witnessCount = 0;
  witnessOffset = 0;
  pagedWitnessVariables: VariableValues[] = [];
  readonly variablesPageSize = 20;
  results_per_page = 20;
  loadingVariables = true;

  inactiveContentVariables: string[] = [];
  private pendingContent: string | null = null;
  private variablesLoaded = false;
  private variablesByToken = new Map<
    string,
    { type: string; status: boolean }
  >();
  private colorCache = new Map<string, string>();
  private formatsByType = new Map<string, VariableFormat>();
  private variableFormatKeys: VariableFormat[] | null = null;

  private editor: DraftEditor | null = null;
  private lastSelection: { index: number; length: number } | null = null;
  private insertedVariables = new Set<string>();
  private variablesRequestId = 0;
  private witnessVariablesRequestId = 0;
  private witnessesSnapshot = '[]';
  private persistedAsDraft = true;
  private witnessVariablesSynced = false;
  loadingWitnessVariables = false;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private routeParams: ActivatedRoute,
    private router: Router,
    private api: ApiService,
    private toast: ToastService,
    private authService: AuthService,
    private breadcrumbService: BreadcrumbService,
    private viewportScroller: ViewportScroller,
    private elementRef: ElementRef<HTMLElement>,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    if (
      (this.router.url.includes('new') &&
        !this.authService.verifyPermission(['de_can_add_drafts'])) ||
      (this.route.snapshot.params.id &&
        !this.authService.verifyPermission(['de_can_edit_drafts']))
    ) {
      this.toast.show(
        'error',
        'Aviso',
        'Você não tem permissão para acessar essa página.'
      );
      this.router.navigateByUrl('/app/dashboard');
    }

    this.setBreadcrumb();

    this.form.valueChanges.subscribe(() => {
      Utils.getErrors(this.form);
    });

    this.form.controls.content.valueChanges
      .pipe(debounceTime(400))
      .subscribe((value) => {
        this.syncInactiveContentVariables(value);
        this.syncContentVariableFormats();
      });

    this.searchForm.controls.search.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(300))
      .subscribe(() => {
        this.applyVariablesPage(1);
        this.applyWitnessPage(1);
      });

    this.form.controls.witnesses.valueChanges
      .pipe(debounceTime(500))
      .subscribe((value) => {
        const next = JSON.stringify(value ?? []);

        if (next === this.witnessesSnapshot) {
          return;
        }

        this.witnessesSnapshot = next;
        this.syncWitnessVariables();
      });

    this.fetchData();

    this.route.params.subscribe(({ id, fundId }) => {
      if (!this.router.url.includes('new')) {
        this.id = this.routeParams.snapshot.params.id;
        this.setData();
      } else {
        this.id = null;
        this.witnessVariablesSynced = true;
      }
    });

    this.getVariables();
  }

  async fetchData() {
    await Promise.all([
      this.fetchRoles(),
      this.fetchTypes(),
      this.fetchWitnesses(),
    ]);

    this.loading = false;
  }

  async fetchRoles() {
    try {
      const response = await this.api.get<Role[]>({
        route: 'api/roles',
        token: true,
      });

      const acceptedApplicables = ['shareholder', 'assignor', 'provider'];

      const mappedData = response
        .filter((item) => acceptedApplicables.includes(item.applicable))
        .map((item) => ({
          label: `${item.applicable_display} - ${item.name}`,
          value: item.id,
        }))
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        });

      this.applicableItems = mappedData;
    } catch (error) {
      console.log(error);

      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os dados de papeis.'
      );
    }
  }

  async fetchTypes() {
    try {
      const response = await this.api.get({
        route: 'api/document-type/options/',
        token: true,
        params: { status: 'active' },
      });

      const types = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.results)
            ? response.results
            : [];

      this.types = types;

      this.typeItems = types
        .map((item) => ({
          label: item.name ?? item.label ?? item.type_document,
          value: item.id ?? item.value,
        }))
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        });
    } catch (error) {
      console.log(error);

      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os dados de tipos de documento.'
      );
    }
  }

  async fetchWitnesses() {
    try {
      const response = await this.api.get({
        route: 'api/minute-witnesses/',
        token: true,
      });

      const witnesses = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.results)
            ? response.results
            : [];

      this.witnessItems = witnesses
        .map((item) => ({
          label: item.name ?? item.label,
          value: item.id ?? item.value,
        }))
        .filter((item) => item.label && item.value != null)
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        });
    } catch (error) {
      console.log(error);

      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar as testemunhas.'
      );
    }
  }

  async setData() {
    try {
      const { data } = await this.api.get({
        route: `api/draft/${this.id}/`,
        token: true,
      });

      const { content, ...fields } = data;

      this.form.patchValue({
        ...fields,
        type: data.type.id,
        assignees: this.normalizeIds(data.roles),
        witnesses: this.normalizeIds(data.witnesses),
        final_date: data.final_date
          ? data.final_date.split('-').reverse().join('/')
          : null,
      });

      this.pendingContent = content ?? '';
      this.title = data.name;
      this.persistedAsDraft = data.is_draft !== false;
      this.witnessesSnapshot = JSON.stringify(
        this.form.controls.witnesses.value ?? []
      );
      this.applyColoredContent();

      if (this.normalizeIds(data.witnesses).length && this.id) {
        this.refreshWitnessVariables(this.id);
      } else {
        this.witnessVariablesSynced = true;
        this.syncInactiveContentVariables(content ?? '');
      }
    } catch (error) {
      console.error(error);
    }
  }
  getBackLink() {
    if (this.fundId) {
      return `/app/funds/approval/${this.fundId}`;
    } else {
      return '/app/drafts-old';
    }
  }

  redirect() {
    if (this.fundId) {
      this.router.navigate([`/app/funds/approval/${this.fundId}`], {
        state: { step: 9 },
      });
    } else {
      this.router.navigateByUrl(draftsTabUrl('models'));
    }
  }

  getDisabled() {
    return !this.form.valid;
  }

  async createTemplate(isDraft = false) {
    if (!isDraft && this.inactiveContentVariables.length) {
      return;
    }

    const isNew = !this.id;
    const saved = await this.saveTemplate(isDraft);

    if (saved) {
      this.toast.show(
        'info',
        'Sucesso',
        isNew
          ? 'Um novo template foi criado e já está disponível para ser utilizado pelo sistema'
          : 'Template atualizado, já está disponível para ser utilizado pelo sistema'
      );
      this.redirect();
    }
  }

  async previewPrint() {
    if (
      this.getDisabled() ||
      this.previewing ||
      this.inactiveContentVariables.length
    ) {
      return;
    }

    this.previewing = true;

    try {
      const draftId = await this.saveTemplate(true);

      if (!draftId) {
        return;
      }

      const token = this.authService.getToken();
      const headers = new HttpHeaders({
        Authorization: `Bearer ${token}`,
      });

      const response = await this.http
        .get(`${this.api.url}/api/draft/${draftId}/preview/`, {
          headers,
          responseType: 'blob',
        })
        .toPromise();

      if (!response) {
        throw new Error('empty-preview');
      }

      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank')?.focus();
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao visualizar a impressão.'
      );
    } finally {
      this.previewing = false;
    }
  }

  private async saveTemplate(
    isDraft = false,
    silent = false
  ): Promise<number | null> {
    try {
      const { assignees, ...formValue } = this.form.value;
      const payload = {
        ...formValue,
        roles: assignees ?? [],
        content: this.stripVariableStyles(this.form.controls.content.value),
        final_date: this.form.controls.final_date.value
          ? this.form.controls.final_date.value.split('/').reverse().join('-')
          : null,
        is_draft: isDraft,
      };

      const response = this.id
        ? await this.api.patch({
            route: `api/draft/${this.id}/`,
            token: true,
            body: payload,
          })
        : await this.api.post({
            route: `api/draft/`,
            token: true,
            body: payload,
          });

      const data = response?.data ?? response;
      const id = data?.id ?? this.id;

      if (id) {
        this.id = id;
        this.title = this.form.controls.name.value || this.title;
      }

      return id ?? null;
    } catch (error) {
      console.error(error);

      if (!silent) {
        this.toast.show(
          'error',
          'Erro',
          'Ocorreu um erro ao salvar o template.'
        );
      }

      return null;
    }
  }

  private async syncWitnessVariables() {
    const requestId = ++this.witnessVariablesRequestId;
    this.loadingWitnessVariables = true;

    try {
      const id = await this.saveTemplate(
        this.id ? this.persistedAsDraft : true,
        true
      );

      if (!id || requestId !== this.witnessVariablesRequestId) {
        return;
      }

      await this.fetchWitnessVariables(id, requestId);
    } catch (error) {
      console.error(error);
    } finally {
      if (requestId === this.witnessVariablesRequestId) {
        this.loadingWitnessVariables = false;
      }
    }
  }

  private refreshWitnessVariables(draftId: number | string) {
    const requestId = ++this.witnessVariablesRequestId;
    this.loadingWitnessVariables = true;

    this.fetchWitnessVariables(draftId, requestId)
      .catch((error) => console.error(error))
      .finally(() => {
        if (requestId === this.witnessVariablesRequestId) {
          this.loadingWitnessVariables = false;
        }
      });
  }

  private async fetchWitnessVariables(
    draftId: number | string,
    requestId: number
  ) {
    const response = await this.api.get({
      route: 'api/variables/',
      token: true,
      params: { draft_document: draftId },
    });

    if (requestId !== this.witnessVariablesRequestId) {
      return;
    }

    this.witnessVariables = this.normalizeVariables(response).filter((item) =>
      this.isWitnessVariable(item)
    );
    this.witnessVariablesSynced = true;
    this.applyWitnessPage(1);
    this.rebuildVariablesByToken();
    this.syncInactiveContentVariables(this.form.controls.content.value);
    this.syncContentVariableFormats();
  }

  onVariablesTab(index: number) {
    this.variablesTabIndex = index;
  }

  get emptyVariablesMessage() {
    return this.variablesTabIndex === 0
      ? 'Nenhuma variável encontrada'
      : 'Nenhuma variável de testemunha. Selecione testemunhas para gerá-las.';
  }

  get showVariableSkeleton() {
    return this.variablesTabIndex === 0
      ? this.loadingVariables
      : this.loadingWitnessVariables;
  }

  get activeVariableRows(): VariableValues[] {
    return this.variablesTabIndex === 0
      ? this.variables
      : this.pagedWitnessVariables;
  }

  get filteredWitnessVariables(): VariableValues[] {
    const search = this.variableSearch();
    const available = this.witnessVariables.filter((item) => item.status);

    if (!search) {
      return available;
    }

    return available.filter((item) => this.matchesVariableSearch(item, search));
  }

  private variableSearch() {
    return String(this.searchForm.controls.search.value ?? '')
      .trim()
      .toLowerCase();
  }

  private matchesVariableSearch(item: VariableValues, search: string) {
    return [item.name, item.variable, item.description].some((value) =>
      String(value ?? '')
        .toLowerCase()
        .includes(search)
    );
  }

  private isWitnessVariable(item: { id?: unknown }) {
    return String(item.id ?? '').startsWith('witness-');
  }

  private rebuildVariablesByToken() {
    this.variablesByToken = new Map(
      [...this.allVariables, ...this.witnessVariables]
        .filter((item) => item.variable)
        .map((item) => [
          this.decodeTextEntities(item.variable),
          { type: item.type, status: item.status },
        ])
    );
  }

  async getVariables() {
    const requestId = ++this.variablesRequestId;
    this.loadingVariables = true;

    try {
      const response = await this.api.get({
        route: 'api/variables/',
        token: true,
      });

      if (requestId !== this.variablesRequestId) {
        return;
      }

      this.allVariables = this.normalizeVariables(response);
      this.rebuildVariablesByToken();
      this.applyVariablesPage(1);
    } catch (error) {
      console.log(error);
    }

    if (requestId === this.variablesRequestId) {
      this.loadingVariables = false;
      this.variablesLoaded = true;
      this.applyColoredContent();
      this.syncContentVariableFormats();
    }
  }

  changePageData(page: number) {
    if (this.variablesTabIndex === 0) {
      this.applyVariablesPage(page);
      return;
    }

    this.applyWitnessPage(page);
  }

  private applyVariablesPage(page: number) {
    const pageData = this.paginate(this.filteredVariables(), page);

    this.variablesPage = pageData.page;
    this.count = pageData.count;
    this.results_per_page = this.variablesPageSize;
    this.variables = pageData.rows;
    this.offset = pageData.offset;
  }

  private applyWitnessPage(page: number) {
    const pageData = this.paginate(this.filteredWitnessVariables, page);

    this.witnessPage = pageData.page;
    this.witnessCount = pageData.count;
    this.pagedWitnessVariables = pageData.rows;
    this.witnessOffset = pageData.offset;
  }

  private paginate(items: VariableValues[], page: number) {
    const totalPages = Math.max(
      1,
      Math.ceil(items.length / this.variablesPageSize)
    );
    const currentPage = Math.min(Math.max(page, 1), totalPages);
    const start = (currentPage - 1) * this.variablesPageSize;
    const rows = items.slice(start, start + this.variablesPageSize);

    return {
      page: currentPage,
      count: items.length,
      offset: rows.length ? start + rows.length : 0,
      rows,
    };
  }

  private filteredVariables() {
    const search = this.variableSearch();
    const available = this.allVariables.filter((item) => item.status);

    if (!search) {
      return available;
    }

    return available.filter((item) => this.matchesVariableSearch(item, search));
  }

  private normalizeVariables(response: unknown): VariableValues[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (response && typeof response === 'object') {
      const data = response as { results?: unknown; data?: unknown };

      if (Array.isArray(data.results)) {
        return data.results;
      }

      if (Array.isArray(data.data)) {
        return data.data;
      }
    }

    return [];
  }

  handleBack() {
    if (this.showResume) {
      this.showResume = false;
      this.showForm = true;
      this.scrollToTop();
    }
  }

  handleNext() {
    if (this.inactiveContentVariables.length) {
      return;
    }

    if (this.showResume) {
      this.createTemplate();
      return;
    }

    this.showForm = false;
    this.showResume = true;
    this.scrollToTop();
  }

  private scrollToTop(): void {
    setTimeout(() => {
      this.viewportScroller.scrollToPosition([0, 0]);
      window.scrollTo(0, 0);

      const cardBody = this.elementRef.nativeElement.querySelector(
        '.dash-card-body'
      );

      if (cardBody) {
        cardBody.scrollTop = 0;
      }
    });
  }

  renderType(typeId: number) {
    const type = this.typeItems.find((item) => item.value === typeId);
    return type ? type.label : '-';
  }

  get selectedAssignees(): { id: number; label: string }[] {
    const selected = this.form.controls.assignees.value || [];

    return selected.map((id) => {
      const role = this.applicableItems.find((item) => item.value === id);
      return {
        id,
        label: role ? role.label : 'Papel não encontrado',
      };
    });
  }

  renderWitnesses() {
    const selected = this.form.controls.witnesses.value || [];

    if (!selected.length) {
      return '-';
    }

    return selected
      .map((id) => {
        const witness = this.witnessItems.find((item) => item.value === id);
        return witness ? witness.label : id;
      })
      .join(', ');
  }

  private normalizeIds(values: unknown): number[] {
    if (!Array.isArray(values)) {
      return [];
    }

    return values
      .map((value) => {
        if (typeof value === 'number') {
          return value;
        }

        if (value && typeof value === 'object' && 'id' in value) {
          return (value as { id: number }).id;
        }

        return null;
      })
      .filter((id) => id !== null);
  }

  removeAssignee(index: number) {
    const selected = [...(this.form.controls.assignees.value || [])];
    selected.splice(index, 1);
    this.form.controls.assignees.setValue(selected);
  }

  onEditorCreated(editor: DraftEditor): void {
    this.editor = editor;
    editor?.on('selection-change', (range) => {
      if (range) {
        this.lastSelection = range;
      }
    });
  }

  insertVariable(variable: string, type = 'data_engine') {
    if (!this.showForm) {
      return;
    }
    this.insertedVariables.add(variable);
    const editor = this.editor;

    if (!editor) {
      const currentContent = this.form.controls.content.value || '';
      this.form.controls.content.setValue(currentContent + variable);
      return;
    }

    const range = editor.getSelection(true) ?? this.lastSelection;
    let index = range?.index ?? Math.max(editor.getLength() - 1, 0);

    if (range?.length) {
      editor.deleteText(index, range.length, 'user');
    }

    if (this.needsSpaceBefore(editor, index)) {
      this.insertPlainSpace(editor, index);
      index += 1;
    }

    editor.insertText(index, variable, this.getVariableFormats(type), 'user');
    index += variable.length;

    if (this.needsSpaceAfter(editor, index)) {
      this.insertPlainSpace(editor, index);
      index += 1;
    }

    editor.setSelection(index, 0, 'user');
    editor.format('color', false, 'user');
    editor.format('background', false, 'user');
    editor.focus();
  }

  private needsSpaceBefore(
    editor: DraftEditor,
    index: number
  ): boolean {
    if (index <= 0) {
      return false;
    }

    const charBefore = editor.getText(index - 1, 1);
    return charBefore !== '\n' && !this.isSpaceChar(charBefore);
  }

  private needsSpaceAfter(
    editor: DraftEditor,
    index: number
  ): boolean {
    const charAfter = editor.getText(index, 1);
    return !this.isSpaceChar(charAfter);
  }

  private isSpaceChar(char: string): boolean {
    return char === ' ' || char === '\u00A0';
  }

  private insertPlainSpace(
    editor: DraftEditor,
    index: number
  ): void {
    editor.insertText(index, ' ', { color: false, background: false }, 'user');
    editor.removeFormat(index, 1, 'user');
  }

  private applyColoredContent() {
    if (this.pendingContent == null || !this.variablesLoaded) {
      return;
    }

    this.form.controls.content.setValue(
      this.colorizeContentVariables(this.pendingContent)
    );
    this.pendingContent = null;
  }

  private colorizeContentVariables(html: string): string {
    if (!html || !this.variablesByToken.size) {
      return html;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');

    this.collectTextNodes(doc.body).forEach((textNode) => {
      const text = this.decodeTextEntities(textNode.textContent ?? '');
      const fragment = doc.createDocumentFragment();
      let cursor = 0;

      this.forEachVariableToken(text, (token, index) => {
        const format = this.formatForToken(token);

        if (index > cursor) {
          fragment.append(text.slice(cursor, index));
        }

        fragment.append(
          format ? this.createVariableSpan(doc, token, format) : token
        );
        cursor = index + token.length;
      });

      if (!cursor) {
        return;
      }

      fragment.append(text.slice(cursor));
      textNode.replaceWith(fragment);
    });

    return doc.body.innerHTML;
  }

  private createVariableSpan(
    doc: Document,
    token: string,
    format: VariableFormat
  ) {
    const span = doc.createElement('span');

    span.style.color = format.color;
    span.style.backgroundColor = format.background;
    span.textContent = token;

    return span;
  }

  private collectTextNodes(root: Node): Text[] {
    const nodes: Text[] = [];
    const walker = root.ownerDocument.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT
    );
    let current = walker.nextNode();

    while (current) {
      nodes.push(current as Text);
      current = walker.nextNode();
    }

    return nodes;
  }

  private decodeTextEntities(value: string): string {
    if (!value.includes('&')) {
      return value;
    }

    const doc = new DOMParser().parseFromString(value, 'text/html');
    return doc.body.textContent ?? value;
  }

  private syncInactiveContentVariables(html: string | null) {
    if (!html) {
      this.inactiveContentVariables = [];
      return;
    }

    const text =
      new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';
    const found = new Set<string>();

    this.forEachVariableToken(text, (token) => {
      const variable = this.variablesByToken.get(token);

      if ((variable && !variable.status) || this.isUnavailableWitnessToken(token)) {
        found.add(token);
      }
    });

    this.inactiveContentVariables = Array.from(found);
  }

  private syncContentVariableFormats() {
    const editor = this.editor;

    if (!editor || !this.variablesLoaded) {
      return;
    }

    const ops = editor.getContents().ops ?? [];
    const { text, toQuill } = this.readEditorText(ops);
    const ranges = this.variableRanges(text, toQuill);
    const clears: Array<{ index: number; length: number }> = [];
    const applies: FormatRange[] = [];
    let index = 0;

    ops.forEach((op) => {
      if (typeof op.insert !== 'string') {
        index += 1;
        return;
      }

      const end = index + op.insert.length;
      const hasOurFormat = this.isOurVariableFormat(
        op.attributes?.color,
        op.attributes?.background
      );
      let cursor = index;

      ranges.forEach((range) => {
        const rangeEnd = range.index + range.length;

        if (rangeEnd <= cursor || range.index >= end) {
          return;
        }

        const overlapStart = Math.max(range.index, index);
        const overlapEnd = Math.min(rangeEnd, end);

        if (hasOurFormat && cursor < overlapStart) {
          clears.push({ index: cursor, length: overlapStart - cursor });
        }

        if (
          !this.sameColor(op.attributes?.color, range.color) ||
          !this.sameColor(op.attributes?.background, range.background)
        ) {
          applies.push({
            index: overlapStart,
            length: overlapEnd - overlapStart,
            color: range.color,
            background: range.background,
          });
        }

        cursor = Math.max(cursor, overlapEnd);
      });

      if (hasOurFormat && cursor < end) {
        clears.push({ index: cursor, length: end - cursor });
      }

      index = end;
    });

    this.paintVariableFormats(editor, clears, applies);
    this.clearVariableFormatAtCursor(editor, ranges);
  }

  private readEditorText(
    ops: Array<{ insert?: string | Record<string, unknown> }>
  ) {
    let text = '';
    const embedAt: number[] = [];

    ops.forEach((op) => {
      if (typeof op.insert === 'string') {
        text += op.insert;
        return;
      }

      embedAt.push(text.length);
    });

    return {
      text,
      toQuill: (offset: number) =>
        offset + embedAt.filter((at) => at <= offset).length,
    };
  }

  private variableRanges(
    text: string,
    toQuill: (offset: number) => number
  ): FormatRange[] {
    const ranges: FormatRange[] = [];

    this.forEachVariableToken(text, (token, offset) => {
      const format = this.formatForToken(token);

      if (!format) {
        return;
      }

      ranges.push({
        index: toQuill(offset),
        length: token.length,
        ...format,
      });
    });

    return ranges;
  }

  private forEachVariableToken(
    text: string,
    visit: (token: string, index: number) => void
  ) {
    const pattern = /<\$[A-Za-z0-9_]+>/g;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text))) {
      visit(match[0], match.index);
    }
  }

  private formatForToken(token: string) {
    const variable = this.variablesByToken.get(token);

    if (variable) {
      return this.getVariableFormats(variable.status ? variable.type : 'error');
    }

    if (this.isUnavailableWitnessToken(token)) {
      return this.getVariableFormats('error');
    }

    return null;
  }

  private isUnavailableWitnessToken(token: string) {
    return (
      this.witnessVariablesSynced &&
      this.isWitnessToken(token) &&
      !this.variablesByToken.has(token)
    );
  }

  private isWitnessToken(token: string) {
    return /^<\$TESTEMUNHA_\d+_[A-Z0-9_]+>$/i.test(token);
  }

  private paintVariableFormats(
    editor: DraftEditor,
    clears: Array<{ index: number; length: number }>,
    applies: FormatRange[]
  ) {
    if (!clears.length && !applies.length) {
      return;
    }

    const selection = editor.getSelection();

    clears.forEach((range) => {
      editor.formatText(
        range.index,
        range.length,
        { color: false, background: false },
        'silent'
      );
    });
    applies.forEach((range) => {
      editor.formatText(
        range.index,
        range.length,
        { color: range.color, background: range.background },
        'silent'
      );
    });

    if (selection) {
      editor.setSelection(selection.index, selection.length, 'silent');
    }
  }

  private clearVariableFormatAtCursor(editor: DraftEditor, ranges: FormatRange[]) {
    const selection = editor.getSelection();
    const inside = ranges.some(
      (range) =>
        selection &&
        !selection.length &&
        selection.index >= range.index &&
        selection.index < range.index + range.length
    );

    if (!selection || selection.length || inside) {
      return;
    }

    const format = editor.getFormat(selection.index, 0);

    if (!this.isOurVariableFormat(format.color, format.background)) {
      return;
    }

    editor.format('color', false, 'silent');
    editor.format('background', false, 'silent');
  }

  private isOurVariableFormat(color?: string, background?: string) {
    if (!color || !background) {
      return false;
    }

    const currentColor = this.colorKey(color);
    const currentBackground = this.colorKey(background);

    return this.getVariableFormatKeys().some(
      (format) =>
        format.color === currentColor && format.background === currentBackground
    );
  }

  private getVariableFormatKeys() {
    if (!this.variableFormatKeys) {
      this.variableFormatKeys = ['error', 'fs_engine', 'data_engine'].map(
        (type) => {
          const format = this.getVariableFormats(type);

          return {
            color: this.colorKey(format.color),
            background: this.colorKey(format.background),
          };
        }
      );
    }

    return this.variableFormatKeys;
  }

  private sameColor(current?: string, expected?: string) {
    if (!current || !expected) {
      return !current && !expected;
    }

    return this.colorKey(current) === this.colorKey(expected);
  }

  private colorKey(value: string) {
    const cached = this.colorCache.get(value);

    if (cached) {
      return cached;
    }

    const probe = document.createElement('span');
    probe.style.color = value;
    document.body.append(probe);
    const key = getComputedStyle(probe).color;
    probe.remove();
    this.colorCache.set(value, key);

    return key;
  }

  private getVariableFormats(type: string): VariableFormat {
    const cached = this.formatsByType.get(type);

    if (cached) {
      return cached;
    }

    const format = this.resolveVariableFormats(type);
    this.formatsByType.set(type, format);

    return format;
  }

  private resolveVariableFormats(type: string): VariableFormat {
    if (type === 'error') {
      return {
        color: this.getCssVar('--danger', '#da1e28'),
        background: this.getCssVar('--danger-lighter', '#fff1f1'),
      };
    }

    const isSmart = type === 'fs_engine';

    if (isSmart) {
      return {
        color: this.getCssVar('--badge-info-text', '#0043CE'),
        background: this.getCssVar('--badge-info-bg', '#D0E2FF'),
      };
    }

    return {
      color: this.getCssVar('--tag-accent-text', '#9f1853'),
      background: this.getCssVar('--tag-accent-bg', '#ffd6e8'),
    };
  }

  private getCssVar(name: string, fallback: string): string {
    const fromRoot = getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();

    if (fromRoot) {
      return fromRoot;
    }

    return (
      getComputedStyle(document.body).getPropertyValue(name).trim() || fallback
    );
  }

  private stripVariableStyles(html: string | null): string | null {
    if (!html) {
      return html;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    let changed = true;
    let safety = 0;

    while (changed && safety < 10) {
      changed = false;
      safety += 1;

      Array.from(doc.querySelectorAll('span')).forEach((span) => {
        if (!this.isVariableToken(span.textContent ?? '')) {
          return;
        }

        span.replaceWith(doc.createTextNode(span.textContent ?? ''));
        changed = true;
      });
    }

    return doc.body.innerHTML;
  }

  private isVariableToken(text: string): boolean {
    const trimmed = text.trim();

    if (!trimmed) {
      return false;
    }

    return (
      this.insertedVariables.has(trimmed) || /^<\$[A-Za-z0-9_]+>$/.test(trimmed)
    );
  }

  setBreadcrumb(): void {
    const canViewDrafts =
      this.authService.verifyPermission('de_can_view_drafts');
    const isNew = this.router.url.includes('new');
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Minutas',
        url: DRAFTS_PAGE_URL,
        clickable: canViewDrafts,
      },
      {
        label: 'Templates de minutas',
        url: draftsTabUrl('models'),
        clickable: canViewDrafts,
      },
      {
        label: isNew ? 'Novo modelo' : 'Editar modelo',
        url: this.router.url,
        clickable: false, // Página atual, não precisa ser clicável
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }
}
